import { createPortal } from 'react-dom';
import { LayoutGrid, Layers, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

type EntityDetailsModalProps = {
	isOpen: boolean;
	onClose: () => void;
	entityName: string;
	entityDescription: string;
	entitySummary?: string | null;
	entitySummaryLabel?: string;
	initials: string;
	iconClassName: string;
	listTitle: string;
	childItems: string[];
	emptyTitle: string;
	emptyDescription: string;
	createChildText: string;
	onEditChild: (childIndex: number) => void;
	onDeleteChild: (childIndex: number) => void;
	onCreateChild?: () => void;
};

export const EntityDetailsModal = ({
	isOpen,
	onClose,
	entityName,
	entityDescription,
	entitySummary,
	entitySummaryLabel = 'Descrição',
	initials,
	iconClassName,
	listTitle,
	childItems,
	emptyTitle,
	emptyDescription,
	createChildText,
	onEditChild,
	onDeleteChild,
	onCreateChild,
}: EntityDetailsModalProps) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-start justify-between gap-4 px-8 py-6">
					<div className="flex min-w-0 gap-4">
						<div
							className={cn(
								'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-black',
								iconClassName,
							)}
						>
							{initials}
						</div>
						<div className="min-w-0 space-y-1">
							<h2 className="text-lg font-black text-stone-900">
								{entityName}
							</h2>
							<p className="text-sm text-stone-500">
								{entityDescription}
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
						aria-label="Fechar"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto px-8 py-6">
					{entitySummary != null && (
						<div className="mb-4">
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								{entitySummaryLabel}
							</p>
							<p className="mt-1 text-sm leading-relaxed text-stone-700">
								{entitySummary.trim() || 'Sem descrição.'}
							</p>
						</div>
					)}
					<div className="mb-4 flex items-center gap-2 text-stone-900">
						<LayoutGrid
							className="size-5 text-stone-500"
							strokeWidth={2}
						/>
						<span className="text-sm font-bold">{listTitle}</span>
					</div>

					{childItems.length === 0 ? (
						<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 px-6 py-14 text-center">
							<div className="mb-4 rounded-full bg-white p-4 text-stone-300 shadow-sm">
								<Layers className="size-8" strokeWidth={1.75} />
							</div>
							<h3 className="text-base font-bold text-stone-900">
								{emptyTitle}
							</h3>
							<p className="mx-auto mt-1 max-w-sm text-sm leading-relaxed text-stone-500">
								{emptyDescription}
							</p>
							{onCreateChild && (
								<Button
									type="button"
									text={createChildText}
									variant="outline"
									className="mt-4 rounded-full border-stone-300 px-5 py-2 font-bold"
									startIcon={
										<Plus className="size-4 stroke-[3]" />
									}
									onClick={onCreateChild}
								/>
							)}
						</div>
					) : (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{childItems.map((child, index) => (
								<div
									key={`${child}-${index}`}
									className="flex items-center gap-1 rounded-xl border border-stone-200 bg-white px-2 py-2 pl-3 transition-colors hover:border-stone-300"
								>
									<button
										type="button"
										className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-stone-800"
										onClick={() => onEditChild(index)}
									>
										{child}
									</button>
									<button
										type="button"
										onClick={() => onEditChild(index)}
										className="shrink-0 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
										aria-label={`Editar ${child}`}
									>
										<Pencil size={16} strokeWidth={2} />
									</button>
									<button
										type="button"
										onClick={() => onDeleteChild(index)}
										className="shrink-0 rounded-full p-2 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
										aria-label={`Excluir ${child}`}
									>
										<Trash2 size={16} strokeWidth={2} />
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				<div className="flex shrink-0 justify-end px-8 py-6">
					<Button
						type="button"
						text="Fechar"
						onClick={onClose}
						className="rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
					/>
				</div>
			</div>
		</div>,
		document.body,
	);
};
